import React, { useState, useMemo, useRef, useEffect } from 'react';
import { CheckCircle, Truck, LogOut, ChevronDown, ChevronUp, X, RotateCcw, Camera, Keyboard, Bell, BellOff, Download, ChevronRight } from 'lucide-react';
import CameraScanner from './CameraScanner';

const DriverMobileView = ({ currentUser, orders = [], setOrders, cylinders = [], setCylinders, deliveries = [], setDeliveries, collections = [], setCollections, customers = [], items = [], specialPrices = [], warehouses = [], onLogout, notifStatus, onToggleNotif }) => {
  const [showCompleted, setShowCompleted] = useState(false);
  const [inputMode,    setInputMode]    = useState(null);  // 직접 입력 모드 orderId
  const [scannedMap,   setScannedMap]   = useState({});    // { orderId: ['바코드', ...] }
  const [barcodeInput, setBarcodeInput] = useState('');
  const scanInputRef = useRef(null);
  const today = new Date().toISOString().split('T')[0];

  // 회수 관련 상태
  const [showCollection, setShowCollection]         = useState(false);
  const [colCustomer,    setColCustomer]             = useState('');
  const [colScanned,     setColScanned]             = useState([]);
  const [colBarcodeInput, setColBarcodeInput]       = useState('');
  const [colInputMode,   setColInputMode]           = useState(false);
  const [colError,       setColError]               = useState('');
  const [colSaved,       setColSaved]               = useState(false);
  const colBarcodeRef = useRef(null);
  const targetWarehouse = warehouses[0] ?? '제1 창고(메인)';

  const myOrders = useMemo(
    () => orders.filter(o => o.assignedDriverId === currentUser.id),
    [orders, currentUser.id]
  );
  const pendingOrders   = myOrders.filter(o => o.status !== 'delivered');
  const completedOrders = myOrders.filter(o => o.status === 'delivered');

  // 직접 입력 모드 열릴 때 자동 포커스
  useEffect(() => {
    if (inputMode && scanInputRef.current) {
      setTimeout(() => scanInputRef.current?.focus(), 100);
    }
  }, [inputMode]);

  useEffect(() => {
    if (colInputMode && colBarcodeRef.current) {
      setTimeout(() => colBarcodeRef.current?.focus(), 100);
    }
  }, [colInputMode]);

  // ── 배송 스캔 ──────────────────────────────────────────────
  const handleScan = (orderId, value) => {
    const val = value.trim();
    if (!val) return;
    setScannedMap(prev => {
      const existing = prev[orderId] || [];
      if (existing.includes(val)) return prev;
      return { ...prev, [orderId]: [...existing, val] };
    });
  };

  const handleKeyDown = (e, orderId) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleScan(orderId, barcodeInput);
      setBarcodeInput('');
    }
  };

  const removeBarcode = (orderId, idx) => {
    setScannedMap(prev => ({
      ...prev,
      [orderId]: (prev[orderId] || []).filter((_, i) => i !== idx),
    }));
  };

  const resetScan = (orderId) => {
    setScannedMap(prev => ({ ...prev, [orderId]: [] }));
    setBarcodeInput('');
  };

  const markDelivered = (orderId, quantity) => {
    const barcodes = scannedMap[orderId] || [];
    if (barcodes.length < quantity) return;

    const order = orders.find(o => o.id === orderId);
    if (!order) return;

    const knownBarcodes = barcodes.filter(bc => cylinders.find(c => c.id === bc));
    if (knownBarcodes.length > 0) {
      setCylinders(prev => prev.map(c =>
        knownBarcodes.includes(c.id)
          ? { ...c, locType: 'customer', location: order.customer }
          : c
      ));
    }

    const customerObj = customers.find(c => c.name === order.customer);
    let price = 0;
    if (customerObj) {
      const sp = specialPrices.find(s => s.customerId === customerObj.id && s.item === order.item);
      if (sp) price = sp.price;
    }
    if (!price) {
      const itemObj = items.find(i => i.name === order.item);
      price = itemObj?.standardPrice ?? 0;
    }

    const deliveryRecord = {
      id: Date.now(),
      customer: order.customer,
      warehouse: '',
      item: order.item,
      items: { [order.item]: order.quantity },
      quantity: order.quantity,
      price,
      amount: order.quantity * price,
      date: order.deliveryDate || today,
      cylinders: knownBarcodes,
      fromOrderId: orderId,
    };
    setDeliveries(prev => [...prev, deliveryRecord]);

    setOrders(prev => prev.map(o =>
      o.id === orderId
        ? { ...o, status: 'delivered', deliveredBarcodes: barcodes, deliveredAt: new Date().toISOString() }
        : o
    ));
    setInputMode(null);
    setScannedMap(prev => { const n = { ...prev }; delete n[orderId]; return n; });
  };

  // ── 회수 스캔 ──────────────────────────────────────────────
  const processColBarcode = (code) => {
    if (!code) return;
    setColError('');
    if (colScanned.find(s => s.id === code)) {
      setColError(`이미 스캔된 용기입니다: ${code}`); return;
    }
    const cyl = cylinders.find(c => c.id === code);
    if (!cyl) { setColError(`등록되지 않은 바코드: ${code}`); return; }
    // 위치 불일치여도 허용 (음수 재고 지원)
    const notAtCustomer = cyl.locType !== 'customer';
    setColScanned(prev => [...prev, { id: code, item: cyl.item, notAtCustomer }]);
  };

  const handleColKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      processColBarcode(colBarcodeInput.trim());
      setColBarcodeInput('');
    }
  };

  const submitCollection = () => {
    if (!colCustomer) { setColError('거래처를 선택하세요.'); return; }
    if (colScanned.length === 0) { setColError('용기를 스캔하세요.'); return; }

    const returnedIds = colScanned.map(s => s.id);
    setCylinders(prev => prev.map(c =>
      returnedIds.includes(c.id)
        ? { ...c, locType: 'warehouse', location: targetWarehouse, status: '공병' }
        : c
    ));

    const itemCounts = colScanned.reduce((acc, s) => {
      acc[s.item] = (acc[s.item] || 0) + 1;
      return acc;
    }, {});

    const record = {
      id: Date.now(),
      customer: colCustomer,
      item: Object.keys(itemCounts).length === 1 ? Object.keys(itemCounts)[0] : '혼합',
      items: itemCounts,
      quantity: colScanned.length,
      price: 0,
      amount: 0,
      date: today,
      memo: `기사 회수: ${currentUser.name}`,
      targetWarehouse,
      cylinders: returnedIds,
      collectedByDriver: true,
    };
    setCollections(prev => [...prev, record]);

    setColScanned([]);
    setColCustomer('');
    setColBarcodeInput('');
    setColInputMode(false);
    setColError('');
    setColSaved(true);
    setTimeout(() => setColSaved(false), 3000);
  };

  const colItemCounts = colScanned.reduce((acc, s) => {
    acc[s.item] = (acc[s.item] || 0) + 1;
    return acc;
  }, {});

  return (
    <div style={{ background: '#f0f4f8', minHeight: '100vh', fontFamily: 'Pretendard, sans-serif' }}>
      <div style={{ maxWidth: 480, margin: '0 auto' }}>

        {/* 헤더 */}
        <div style={{ background: 'linear-gradient(135deg, #0c2d5e 0%, #185FA5 100%)', padding: '24px 20px 32px', color: 'white' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
            <div>
              <div style={{ fontSize: 11, opacity: 0.65, letterSpacing: 1, marginBottom: 3 }}>DAESUNG ENERGY</div>
              <div style={{ fontSize: 22, fontWeight: 800 }}>{currentUser.name} 기사님</div>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              {notifStatus && notifStatus !== 'unsupported' && (
                <button onClick={onToggleNotif} title={notifStatus === 'granted' ? '알림 끄기' : '배송 알림 켜기'}
                  style={{ background: notifStatus === 'granted' ? 'rgba(255,255,255,0.9)' : 'rgba(255,255,255,0.15)', border: 'none', borderRadius: 10, padding: '9px 12px', color: notifStatus === 'granted' ? '#185FA5' : 'white', cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
                  {notifStatus === 'granted' ? <Bell size={17} /> : <BellOff size={17} />}
                </button>
              )}
              <button onClick={onLogout} style={{ background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: 10, padding: '9px 14px', color: 'white', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 600 }}>
                <LogOut size={15} /> 로그아웃
              </button>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 12 }}>
            <div style={{ background: 'rgba(255,255,255,0.15)', borderRadius: 14, padding: '14px 16px', flex: 1, textAlign: 'center' }}>
              <div style={{ fontSize: 28, fontWeight: 900 }}>{pendingOrders.length}</div>
              <div style={{ fontSize: 12, opacity: 0.8, marginTop: 2 }}>배달 대기</div>
            </div>
            <div style={{ background: 'rgba(255,255,255,0.15)', borderRadius: 14, padding: '14px 16px', flex: 1, textAlign: 'center' }}>
              <div style={{ fontSize: 28, fontWeight: 900 }}>{completedOrders.length}</div>
              <div style={{ fontSize: 12, opacity: 0.8, marginTop: 2 }}>완료</div>
            </div>
          </div>
        </div>

        <div style={{ padding: '20px 16px' }}>

          <div style={{ fontSize: 13, fontWeight: 700, color: '#555', marginBottom: 10 }}>
            배달 목록 ({pendingOrders.length}건)
          </div>

          {myOrders.length === 0 ? (
            <div style={{ background: 'white', borderRadius: 16, padding: '36px 20px', textAlign: 'center', boxShadow: '0 2px 12px rgba(0,0,0,0.05)' }}>
              <Truck size={44} color="#d3d1c7" style={{ margin: '0 auto 14px', display: 'block' }} />
              <div style={{ fontSize: 15, fontWeight: 700, color: '#aaa' }}>배정된 배달이 없습니다</div>
              <div style={{ fontSize: 13, color: '#bbb', marginTop: 6 }}>관리자에게 배달 배정을 요청하세요</div>
            </div>
          ) : pendingOrders.length === 0 ? (
            <div style={{ background: 'white', borderRadius: 16, padding: '36px 20px', textAlign: 'center', boxShadow: '0 2px 12px rgba(0,0,0,0.05)', marginBottom: 16 }}>
              <CheckCircle size={44} color="#059669" style={{ margin: '0 auto 14px', display: 'block' }} />
              <div style={{ fontSize: 16, fontWeight: 800, color: '#059669' }}>모든 배달 완료!</div>
              <div style={{ fontSize: 13, color: '#888', marginTop: 6 }}>수고하셨습니다 {currentUser.name} 기사님 👍</div>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14, marginBottom: 20 }}>
              {pendingOrders.map(o => {
                const scanned = scannedMap[o.id] || [];
                const done    = scanned.length >= o.quantity;
                const isInput = inputMode === o.id;

                return (
                  <div key={o.id} style={{ background: 'white', borderRadius: 18, overflow: 'hidden', boxShadow: '0 2px 14px rgba(0,0,0,0.07)' }}>
                    <div style={{ padding: '18px 18px 16px' }}>

                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: 19, fontWeight: 800, color: '#1e293b', marginBottom: 6 }}>{o.customer}</div>
                          {o.items && Object.keys(o.items).length > 1 ? (
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
                              {Object.entries(o.items).map(([itemName, qty]) => (
                                <span key={itemName} style={{ fontSize: 13, fontWeight: 700, background: '#e8f0fb', color: '#185FA5', padding: '3px 10px', borderRadius: 10 }}>
                                  {itemName} {qty}본
                                </span>
                              ))}
                              <span style={{ fontSize: 12, color: '#888', alignSelf: 'center' }}>총 {o.quantity}본</span>
                            </div>
                          ) : (
                            <div style={{ fontSize: 14, color: '#185FA5', fontWeight: 600 }}>{o.item} {o.quantity}본</div>
                          )}
                        </div>
                        <span style={{ fontSize: 11, fontWeight: 700, background: o.deliveryDate === today ? '#FEF3C7' : '#EEF2FF', color: o.deliveryDate === today ? '#D97706' : '#6366F1', padding: '5px 12px', borderRadius: 20, whiteSpace: 'nowrap', marginLeft: 10 }}>
                          {o.deliveryDate === today ? '오늘 배달' : o.deliveryDate}
                        </span>
                      </div>

                      {o.note && (
                        <div style={{ background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 10, padding: '9px 13px', fontSize: 13, color: '#92400E', marginBottom: 12, lineHeight: 1.5 }}>
                          📌 {o.note}
                        </div>
                      )}

                      {scanned.length > 0 && (
                        <div style={{ background: '#f0f4f8', borderRadius: 12, padding: '10px 12px', marginBottom: 12 }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                            <span style={{ fontSize: 13, fontWeight: 700, color: done ? '#059669' : '#185FA5' }}>
                              스캔 현황: {scanned.length} / {o.quantity}본
                            </span>
                            <button onClick={() => resetScan(o.id)}
                              style={{ background: 'none', border: '1px solid #d3d1c7', borderRadius: 7, padding: '3px 8px', fontSize: 11, color: '#666', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 3 }}>
                              <RotateCcw size={10} /> 초기화
                            </button>
                          </div>
                          <div style={{ width: '100%', height: 5, background: '#d1d9e6', borderRadius: 4, marginBottom: 8 }}>
                            <div style={{ width: `${Math.min(100, (scanned.length / o.quantity) * 100)}%`, height: '100%', background: done ? '#059669' : '#185FA5', borderRadius: 4, transition: 'width 0.3s' }} />
                          </div>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, maxHeight: 120, overflowY: 'auto' }}>
                            {scanned.map((bc, idx) => (
                              <div key={idx} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: 'white', borderRadius: 7, padding: '5px 9px', fontSize: 12 }}>
                                <span style={{ display: 'flex', alignItems: 'center', gap: 5, color: '#333', fontFamily: 'monospace' }}>
                                  <CheckCircle size={12} color="#059669" />{bc}
                                </span>
                                <button onClick={() => removeBarcode(o.id, idx)}
                                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#DC2626', padding: 2, display: 'flex' }}>
                                  <X size={12} />
                                </button>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {isInput && (
                        <div style={{ marginBottom: 10 }}>
                          <input
                            ref={scanInputRef}
                            type="text"
                            value={barcodeInput}
                            onChange={e => setBarcodeInput(e.target.value)}
                            onKeyDown={e => handleKeyDown(e, o.id)}
                            placeholder="바코드 번호 입력 후 Enter"
                            style={{ width: '100%', padding: '12px 14px', border: '2px solid #185FA5', borderRadius: 10, fontSize: 14, boxSizing: 'border-box', background: 'white', outline: 'none' }}
                          />
                        </div>
                      )}

                      {!done && (
                        <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
                          <CameraScanner
                            continuous
                            color="#0c2d5e"
                            onScan={(val) => handleScan(o.id, val)}
                            headerExtra={`· ${scanned.length}/${o.quantity}본`}
                            buttonStyle={{
                              flex: 2, padding: '13px', background: '#0c2d5e', color: 'white',
                              border: 'none', borderRadius: 12, fontSize: 14, fontWeight: 700,
                              cursor: 'pointer', display: 'flex', alignItems: 'center',
                              justifyContent: 'center', gap: 7,
                            }}
                            buttonContent={<><Camera size={18} /> 카메라 스캔</>}
                          />
                          <button
                            onClick={() => { setInputMode(isInput ? null : o.id); setBarcodeInput(''); }}
                            style={{ flex: 1, padding: '13px', background: isInput ? '#e0e7ff' : '#f0f4f8', color: isInput ? '#4F46E5' : '#555', border: isInput ? '1.5px solid #818CF8' : '1.5px solid #d3d1c7', borderRadius: 12, fontSize: 13, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5 }}>
                            <Keyboard size={15} /> 직접입력
                          </button>
                        </div>
                      )}

                      <button
                        onClick={() => markDelivered(o.id, o.quantity)}
                        disabled={!done}
                        style={{ width: '100%', padding: '15px', background: done ? '#059669' : '#d1d9e6', color: done ? 'white' : '#999', border: 'none', borderRadius: 13, fontSize: 16, fontWeight: 700, cursor: done ? 'pointer' : 'not-allowed', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 9, transition: 'background 0.2s' }}>
                        <CheckCircle size={21} />
                        {done ? '배달 완료 확인' : `${o.quantity - scanned.length}본 더 스캔 필요`}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* 완료된 배달 */}
          {completedOrders.length > 0 && (
            <div style={{ marginBottom: 16 }}>
              <button
                onClick={() => setShowCompleted(p => !p)}
                style={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '13px 16px', background: 'white', border: '1px solid #e5e3db', borderRadius: 12, cursor: 'pointer', fontSize: 13, fontWeight: 700, color: '#555', marginBottom: showCompleted ? 10 : 0 }}>
                완료된 배달 ({completedOrders.length}건)
                {showCompleted ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
              </button>
              {showCompleted && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {completedOrders.map(o => (
                    <div key={o.id} style={{ background: 'white', borderRadius: 12, padding: '13px 16px', border: '1px solid #e5e3db', opacity: 0.65 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div>
                          <div style={{ fontSize: 14, fontWeight: 700, color: '#333' }}>{o.customer}</div>
                          <div style={{ fontSize: 12, color: '#888', marginTop: 2 }}>{o.item} {o.quantity}본</div>
                          {o.deliveredBarcodes?.length > 0 && (
                            <div style={{ fontSize: 11, color: '#aaa', marginTop: 3 }}>스캔 {o.deliveredBarcodes.length}건 기록됨</div>
                          )}
                        </div>
                        <span style={{ fontSize: 11, fontWeight: 700, background: '#D1FAE5', color: '#059669', padding: '3px 10px', borderRadius: 20 }}>완료</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ── 용기 회수 등록 ─────────────────────────────── */}
          <div style={{ marginBottom: 32 }}>
            <button
              onClick={() => setShowCollection(p => !p)}
              style={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '15px 18px', background: showCollection ? '#f0fdf8' : 'white', border: `1.5px solid ${showCollection ? '#059669' : '#e5e3db'}`, borderRadius: 14, cursor: 'pointer', fontSize: 14, fontWeight: 700, color: showCollection ? '#059669' : '#444', marginBottom: showCollection ? 12 : 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Download size={17} color={showCollection ? '#059669' : '#888'} />
                용기 회수 등록
              </div>
              {showCollection ? <ChevronUp size={16} /> : <ChevronRight size={16} />}
            </button>

            {showCollection && (
              <div style={{ background: 'white', borderRadius: 16, padding: 20, border: '1.5px solid #059669', boxShadow: '0 2px 14px rgba(5,150,105,0.08)' }}>

                {colSaved && (
                  <div style={{ padding: '12px 16px', background: '#d1fae5', borderRadius: 10, marginBottom: 16, fontSize: 14, fontWeight: 700, color: '#059669', textAlign: 'center' }}>
                    ✓ 회수 등록 완료!
                  </div>
                )}

                {/* 거래처 선택 */}
                <div style={{ marginBottom: 14 }}>
                  <label style={formLabel}>회수처 (거래처)</label>
                  <select
                    value={colCustomer}
                    onChange={e => { setColCustomer(e.target.value); setColError(''); }}
                    style={formInput}>
                    <option value="">거래처 선택</option>
                    {customers.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
                  </select>
                </div>

                {/* 바코드 스캔 */}
                <div style={{ background: '#f0fdf8', border: '1px solid #05966944', borderRadius: 12, padding: 14, marginBottom: 14 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: '#059669', marginBottom: 10 }}>용기 스캔</div>

                  <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
                    <CameraScanner
                      color="#059669"
                      onScan={(code) => { processColBarcode(code); colBarcodeRef.current?.focus(); }}
                      buttonStyle={{
                        flex: 2, padding: '12px', background: '#059669', color: 'white',
                        border: 'none', borderRadius: 10, fontSize: 13, fontWeight: 700,
                        cursor: 'pointer', display: 'flex', alignItems: 'center',
                        justifyContent: 'center', gap: 6,
                      }}
                      buttonContent={<><Camera size={16} /> 카메라 스캔</>}
                    />
                    <button
                      type="button"
                      onClick={() => { setColInputMode(p => !p); setColBarcodeInput(''); }}
                      style={{ flex: 1, padding: '12px', background: colInputMode ? '#e0e7ff' : '#f0f4f8', color: colInputMode ? '#4F46E5' : '#555', border: colInputMode ? '1.5px solid #818CF8' : '1.5px solid #d3d1c7', borderRadius: 10, fontSize: 13, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5 }}>
                      <Keyboard size={14} /> 직접입력
                    </button>
                  </div>

                  {colInputMode && (
                    <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
                      <input
                        ref={colBarcodeRef}
                        type="text"
                        value={colBarcodeInput}
                        onChange={e => { setColBarcodeInput(e.target.value); setColError(''); }}
                        onKeyDown={handleColKeyDown}
                        placeholder="바코드 번호 입력 후 Enter"
                        style={{ flex: 1, padding: '11px 13px', border: '2px solid #059669', borderRadius: 9, fontSize: 14, boxSizing: 'border-box', background: 'white', outline: 'none', fontFamily: 'monospace' }}
                      />
                      <button
                        type="button"
                        onClick={() => { processColBarcode(colBarcodeInput.trim()); setColBarcodeInput(''); colBarcodeRef.current?.focus(); }}
                        style={{ padding: '11px 16px', background: '#059669', color: 'white', border: 'none', borderRadius: 9, fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>
                        추가
                      </button>
                    </div>
                  )}

                  {colError && (
                    <div style={{ fontSize: 12, color: '#DC2626', fontWeight: 600, marginBottom: 8 }}>{colError}</div>
                  )}

                  {colScanned.length > 0 ? (
                    <>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                        <span style={{ fontSize: 13, fontWeight: 700 }}>
                          스캔된 용기 <span style={{ color: '#059669' }}>{colScanned.length}개</span>
                        </span>
                        <button type="button" onClick={() => setColScanned([])}
                          style={{ fontSize: 12, color: '#DC2626', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }}>
                          전체 초기화
                        </button>
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 5, maxHeight: 160, overflowY: 'auto', marginBottom: 10 }}>
                        {colScanned.map((s, i) => (
                          <div key={s.id} style={{ display: 'flex', alignItems: 'center', background: 'white', padding: '7px 10px', borderRadius: 8, border: `1px solid ${s.notAtCustomer ? '#fde68a' : '#cceedd'}`, gap: 8 }}>
                            <CheckCircle size={13} color={s.notAtCustomer ? '#D97706' : '#059669'} />
                            <span style={{ fontFamily: 'monospace', fontSize: 13, fontWeight: 700, flex: 1, color: s.notAtCustomer ? '#92400E' : '#1e293b' }}>{s.id}</span>
                            <span style={{ fontSize: 11, color: '#555', background: s.notAtCustomer ? '#fef3c7' : '#e8faf2', padding: '2px 7px', borderRadius: 10 }}>{s.item}</span>
                            {s.notAtCustomer && (
                              <span style={{ fontSize: 10, color: '#D97706', fontWeight: 700, whiteSpace: 'nowrap' }}>위치불일치</span>
                            )}
                            <button type="button" onClick={() => setColScanned(prev => prev.filter((_, idx) => idx !== i))}
                              style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#bbb', padding: 2, display: 'flex' }}>
                              <X size={13} />
                            </button>
                          </div>
                        ))}
                      </div>
                      {Object.keys(colItemCounts).length > 0 && (
                        <div style={{ padding: '8px 12px', background: 'white', borderRadius: 8, border: '1px solid #cceedd', display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                          {Object.entries(colItemCounts).map(([item, cnt]) => (
                            <span key={item} style={{ fontSize: 12 }}>
                              <span style={{ fontWeight: 700, color: '#059669' }}>{item}</span>
                              <span style={{ color: '#555' }}> {cnt}본</span>
                            </span>
                          ))}
                        </div>
                      )}
                    </>
                  ) : (
                    <div style={{ textAlign: 'center', padding: '12px 0', color: '#bbb', fontSize: 13 }}>스캔된 용기가 없습니다</div>
                  )}
                </div>

                <div style={{ fontSize: 12, color: '#888', marginBottom: 14 }}>
                  입고 창고: <span style={{ fontWeight: 700, color: '#555' }}>{targetWarehouse}</span>
                </div>

                <button
                  onClick={submitCollection}
                  disabled={!colCustomer || colScanned.length === 0}
                  style={{ width: '100%', padding: '15px', background: (colCustomer && colScanned.length > 0) ? '#059669' : '#d1d9e6', color: (colCustomer && colScanned.length > 0) ? 'white' : '#999', border: 'none', borderRadius: 13, fontSize: 15, fontWeight: 700, cursor: (colCustomer && colScanned.length > 0) ? 'pointer' : 'not-allowed', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                  <Download size={19} />
                  {colScanned.length > 0 ? `${colScanned.length}개 회수 완료 등록` : '용기를 스캔하세요'}
                </button>
              </div>
            )}
          </div>

        </div>
      </div>
    </div>
  );
};

const formLabel = { display: 'block', fontSize: 13, fontWeight: 600, color: '#555', marginBottom: 7 };
const formInput = { width: '100%', padding: '12px', border: '1px solid #d3d1c7', borderRadius: 9, fontSize: 14, boxSizing: 'border-box', background: '#fcfbf7' };

export default DriverMobileView;
